import React, { useCallback, useEffect, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { useDispatch } from "react-redux"; // Add this import
import { useNavigate } from "react-router-dom";
import SelectedUserMenu from "../../Assets/images/menu-icons/manage_accounts_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { PermissionCheck } from "../../App/PermissionCheck";
import {
  GetApiMethod,
  PostApiMethod,
  PutApiMethod,
  DeleteApiMethod,
} from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { RoleDto, UserDto, UserFormData } from "../../Dto/Generic";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import { DisplayErrorMessages } from "../../Utils/HelperService";
import { showToaster } from "../../Store/toastSlice";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch";
import UserFormModal from "./UserFormModal";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";

interface UserListDto {
  records: UserDto[];
  totalCount: number;
}

const defaultUserData: UserFormData = {
  id: "",
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  status: false,
  roleId: "",
  roles: [],
  height: "",
  weight: "",
  dateOfBirth: "",
  position: "",
  preferredPosition: "",
  dominantFoot: "",
  skills: "",
  city: "",
  state: "",
  country: "Australia",
  clubId: "",
  leagueId: "",
  nationality: "Australian",
  description: "",
  sports: "",
};

const UserManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userList, setUserList] = useState<UserListDto>({
    records: [],
    totalCount: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [formModal, setFormModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [roles, setRoles] = useState<RoleDto[]>([]);

  const getRoles = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(
        "roles?ismobile=false&page=1&limit=100&sort=displayName&sortDirection=ASC",
      );
      setRoles(res.data.data.records);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setRoles([]);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch]);

  const [initialFormValues, setInitialFormValues] =
    useState<UserFormData>(defaultUserData);

  const getUsers = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));

      const params: any = {
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm.trim(),
        sort: "created_at",
        sortDirection: "DESC",
      };

      if (selectedRole) {
        params.role = selectedRole;
      }

      const queryParams = new URLSearchParams(params).toString();
      const res = await GetApiMethod("user?" + queryParams);

      // Sort by createdAt on frontend in descending order (newest first)
      const sortedUsers = res.data.data.records.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // DESC order (newest first)
      });

      setUserList({
        records: sortedUsers,
        totalCount: res.data.data.totalCount,
      });
      dispatch(showLoader({ showLoader: false }));
    } catch (err) {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, searchTerm, selectedRole]);

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    getUsers();
  }, [getUsers, page, rowsPerPage, searchTerm, selectedRole]);

  const deleteUser = useCallback(
    async (userId: string) => {
      try {
        if (!userId) return;

        dispatch(showLoader({ showLoader: true }));

        // Fetch current user details to satisfy update validator requirements
        const res = await GetApiMethod(`user/${userId}`);
        const data = res?.data?.data || {};

        const payload: any = {
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phone,
          status: data.status,
          isDelete: true,
        };

        await DeleteApiMethod(`user/${userId}`);

        dispatch(showLoader({ showLoader: false }));

        // Refresh user list
        getUsers();

        dispatch(
          showToaster({
            dialogBgColor: "bg-success",
            dialogMsg: "User deleted successfully",
            showDialog: true,
            timer: 3000,
          }),
        );
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));

        const errorMsg = DisplayErrorMessages(error);
        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: 5000,
          }),
        );
      }
    },
    [dispatch, getUsers],
  );

  const resetForm = useCallback(() => {
    setFormModal(false);
    // setProfileImageFile(null);
    setInitialFormValues(defaultUserData);
  }, []);

  const handleEdit = useCallback(async (val: any) => {
    try {
      setFormModal(true);
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(`user/${val.id}`);
      const data = res.data?.data || {};

      setInitialFormValues({
        id: data.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        status: data.status,
        roleId: data.roles[0]?.id || "",
        roles: data.roles,
        height: data.height || "",
        weight: data.userDetail?.weight || "",
        dateOfBirth: data.dateOfBirth || "",
        position: data.position || "",
        preferredPosition: data.userDetail?.preferredPosition || "",
        dominantFoot: data.userDetail?.dominantFoot || "",
        skills: data.userDetail?.skills || "",
        city: data.city || "",
        state: data.state || "",
        country: data.country || "Australia",
        clubId: data.clubId || "",
        leagueId: data.leagueId || "",
        nationality: data.nationality || "",
        description: data.description || "",
        sports: data.sports || "",
        emailVerified: data.emailVerified || false,
      });
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      dispatch(showLoader({ showLoader: false }));
      const errorMsg = DisplayErrorMessages(error);

      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: errorMsg,
          showDialog: true,
          timer: "5000",
        }),
      );
    }
  }, []);

  return (
    <>
      <AuthorizedLayout>
        <BoxHeader
          dashboard_name="User Management"
          button_name={"Add User"}
          image={SelectedUserMenu}
          onAdd={() => {
            setFormModal(true);
            // setProfileImageFile(null);
            setInitialFormValues(defaultUserData);
          }}
          button_show={PermissionCheck("manage_users")}
        >
          <AntTabs value={0}>
            <AntTab label="All Users" className="tab-menu" />
          </AntTabs>
        </BoxHeader>
        <Box display="flex" alignItems="center" gap={4} mt={2}>
          {/* Search Input */}
          <TableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search Users by name, email, phone or role..."
          />

          {/* Role Filter with fixed label */}
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 200 }}>
            <Typography
              variant="caption"
              sx={{ mb: 0.5, color: "text.secondary", fontSize: "inherit" }}
            >
              Filter By Role :
            </Typography>
            <TextField
              select
              value={selectedRole || "null"} // use "null" for All Roles
              onChange={(e) =>
                setSelectedRole(e.target.value === "null" ? "" : e.target.value)
              }
              variant="standard"
              size="small"
              sx={{ minWidth: 200 }}
              SelectProps={{
                renderValue: (selected) => {
                  const val = selected as string;
                  if (val === "null" || val === "") return "All Roles"; // show All Roles
                  const role = roles.find((r) => r.id === val);
                  return role ? role.displayName : "";
                },
              }}
            >
              <MenuItem value="null">All Roles</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Detail</TableCell>

                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userList.records.map((val, key) => (
                <TableRow key={key}>
                  <TableCell>{val.firstName + " " + val.lastName}</TableCell>
                  <TableCell>{val.email}</TableCell>
                  <TableCell>{val.phone}</TableCell>
                  <TableCell>
                    {val.roles.map((role) => role.displayName).join(", ")}
                  </TableCell>
                  <TableCell>{val.status ? "Active" : "Inactive"}</TableCell>
                  <TableCell
                    sx={{
                      textDecoration: "underline",
                      color: "blue",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/user/${val.id}`)}
                  >
                    View
                  </TableCell>

                  <TableCell align="right">
                    <MenuGroup
                      id={val.id}
                      view={false}
                      edit={true}
                      delete={true}
                      clickEdit={() => handleEdit(val)}
                      clickDelete={() => {
                        if (val.id) deleteUser(val.id);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {userList.records.length === 0 && (
                <TableRow>
                  <TableCell align="center">No Data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={userList.totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />

        <CustomModal
          open={formModal}
          onClose={() => {
            resetForm();
            dispatch(showLoader({ showLoader: false }));
            setFormModal(false);
          }}
          header={`${initialFormValues?.id ? "Edit" : "Create"} User`}
          style={ModalStyle({ width: 600, height: "auto" })}
        >
          <UserFormModal
            userData={initialFormValues}
            resetForm={resetForm}
            getUsers={getUsers}
          />
        </CustomModal>
      </AuthorizedLayout>
    </>
  );
};

export default UserManagement;
